import { useState, useEffect, useCallback } from 'react'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import useCustomerList from '../../utils/useCustomerList'
import { listFrom, loadErrorMessage } from '../../utils/loadState'
import { Link } from 'react-router-dom'
import './AdminKnowledge.css'
import { API_BASE as API_BASE_CONFIG } from '../../config'

const API = API_BASE_CONFIG
const tok = () => localStorage.getItem('xlr8_token') || ''
const apiFetch = async (path, opts = {}) => {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json', ...(opts.headers || {}) },
  })
  if (!res.ok) {
    // Carry the server's reason (FastAPI `detail`) into the error the page shows.
    let detail = ''
    try {
      const body = await res.json()
      if (body && body.detail) detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail)
    } catch (parseErr) { detail = '' }
    throw new Error(detail ? `HTTP ${res.status}: ${detail}` : `HTTP ${res.status}`)
  }
  return res.json()
}

// ─── Severity badge ────────────────────────────────────────────────────────────
function SevBadge({ sev }) {
  const cls = { critical: 'sev-critical', warning: 'sev-warning', info: 'sev-info' }[sev] || 'sev-info'
  return <span className={`ak-badge ${cls}`}>{sev || 'info'}</span>
}

// ─── Vendor / Product / Customer selectors shared ─────────────────────────────
function VPCSelectors({ vendors, customers, vendor, product, customer, onChange }) {
  const products = vendors.find(v => v.id === vendor)?.products || []
  return (
    <div className="ak-vpc-row">
      <div className="ak-vpc-field">
        <label>Vendor</label>
        <select value={vendor} onChange={e => onChange('vendor', e.target.value)}>
          <option value="">All Vendors</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
        </select>
      </div>
      <div className="ak-vpc-field">
        <label>Product</label>
        <select value={product} onChange={e => onChange('product', e.target.value)} disabled={!vendor}>
          <option value="">All Products</option>
          {products.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </div>
      <div className="ak-vpc-field">
        <label>Customer</label>
        <select value={customer} onChange={e => onChange('customer', e.target.value)}>
          <option value="">All Customers</option>
          {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.company_name || c.customer_id}</option>)}
        </select>
      </div>
    </div>
  )
}

// ─── Shared test result block ─────────────────────────────────────────────────
function TestResultBlock({ result }) {
  if (!result) return null
  return (
    <div className={`ak-test-result ${result.success ? 'success' : 'error'}`}>
      {result.success ? (
        <>
          <div className="ak-test-ok">✓ {result.count} rows returned</div>
          {result.rows?.length > 0 && (
            <div className="ak-test-preview">
              <table className="ak-table ak-table-sm">
                <thead><tr>{Object.keys(result.rows[0]).map(k => <th key={k}>{k}</th>)}</tr></thead>
                <tbody>{result.rows.slice(0, 5).map((r, i) => (
                  <tr key={i}>{Object.values(r).map((v, j) => <td key={j}>{String(v ?? '—')}</td>)}</tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="ak-test-err">⚠ {result.error}</div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1 — OBSERVATION CHECKS
// ═══════════════════════════════════════════════════════════════════════════════
function ObservationChecksTab({ customers, vendors }) {
  const [scope, setScope] = useState({ vendor: '', product: '', customer: '' })
  const [checks, setChecks] = useState([])
  const [liveObs, setLiveObs] = useState([])
  const [loading, setLoading] = useState(false)
  // A failed load is an error with a Retry, never "No results" (item 201).
  const [checksError, setChecksError] = useState(null)
  const [liveError, setLiveError] = useState(null)
  const [activeView, setActiveView] = useState('catalog')
  const [editing, setEditing] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [saving, setSaving] = useState(false)
  // A failed save or toggle is shown, never read as done (item 201 leftovers).
  const [saveError, setSaveError] = useState(null)
  const [toggleError, setToggleError] = useState(null)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [search, setSearch] = useState('')
  const [catFilter, setCatFilter] = useState('')

  // The check definitions are ukg_pro_checks (GET /api/admin/checks) -- the rows Save and
  // the toggle write back to. A failed load is a LoadError; it used to fall back, silently,
  // to a different table's list (observation_checks), which those writes never touch.
  const loadChecks = useCallback(async () => {
    setLoading(true)
    setChecksError(null)
    try {
      const data = await apiFetch('/api/admin/checks')
      setChecks(listFrom(data, 'checks'))
    } catch (e) {
      console.error(e)
      setChecks([])
      setChecksError(loadErrorMessage(e))
    }
    setLoading(false)
  }, [])

  const loadLiveObs = useCallback(async () => {
    if (!scope.customer) return
    setLoading(true)
    setLiveError(null)
    try {
      const data = await apiFetch(`/api/observations/${scope.customer}?include_dismissed=true`)
      setLiveObs(data?.observations || [])
    } catch (e) {
      console.error(e)
      setLiveObs([])
      setLiveError(loadErrorMessage(e))
    }
    setLoading(false)
  }, [scope.customer])

  useEffect(() => { loadChecks() }, [loadChecks])
  useEffect(() => { if (activeView === 'live') loadLiveObs() }, [activeView, loadLiveObs])

  const openEdit = (item, isLive) => {
    setEditing({ ...item, _isLive: isLive })
    setTestResult(null)
    setSaveError(null)
    if (isLive) {
      setEditForm({
        sql: item.sql || '',
        drill_sql: item.drill_sql || '',
      })
    } else {
      setEditForm({
        title_template: item.title_template || '',
        description: item.description || '',
        severity_default: item.severity_default || 'warning',
        enabled: item.enabled !== false,
        sql_template: item.sql_template || '',
        drill_sql_template: item.drill_sql_template || '',
      })
    }
  }

  const testSQL = async (sqlToTest) => {
    if (!scope.customer) return alert('Select a customer to test against')
    setTesting(true)
    setTestResult(null)
    try {
      const data = await apiFetch('/api/admin/query/raw', {
        method: 'POST',
        body: JSON.stringify({ sql: sqlToTest, customer_id: scope.customer })
      })
      setTestResult({ success: true, rows: data?.data || [], count: data?.row_count || 0 })
    } catch (e) {
      setTestResult({ success: false, error: e.message })
    }
    setTesting(false)
  }

  const saveEdit = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      if (editing._isLive) {
        // PATCH observation row sql/drill_sql
        await apiFetch(`/api/observations/${editing.id}/sql`, {
          method: 'PATCH',
          body: JSON.stringify({ sql: editForm.sql || null, drill_sql: editForm.drill_sql || null })
        })
        await loadLiveObs()
      } else {
        // The definition's fields: the admin-only API writes the listed ukg_pro_checks row
        // (no SQL from the browser). A failure -- including an unknown check -- is shown.
        await apiFetch(`/api/admin/checks/${encodeURIComponent(editing.check_key)}`, {
          method: 'PATCH',
          body: JSON.stringify({
            title_template: editForm.title_template,
            description: editForm.description,
            severity_default: editForm.severity_default,
            enabled: editForm.enabled,
          })
        })
        // The SQL overrides, same row, when they changed. Their failure used to be
        // swallowed as "non-fatal".
        if ((editForm.sql_template || '') !== (editing.sql_template || '')
            || (editForm.drill_sql_template || '') !== (editing.drill_sql_template || '')) {
          await apiFetch(`/api/admin/checks/${encodeURIComponent(editing.check_key)}/sql`, {
            method: 'PATCH',
            body: JSON.stringify({
              sql_template: editForm.sql_template || '',
              drill_sql_template: editForm.drill_sql_template || '',
            })
          })
        }
        await loadChecks()
      }
      setEditing(null)
    } catch (e) {
      setSaveError(`Save failed: ${loadErrorMessage(e)}`)
    }
    setSaving(false)
  }

  const toggleEnabled = async (check) => {
    setToggleError(null)
    try {
      const res = await apiFetch(`/api/admin/checks/${encodeURIComponent(check.check_key)}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: check.enabled === false }),
      })
      // switching a check off dismisses its open findings (item 206); a miss there is shown
      const missed = res?.findings_dismissed?.errors || []
      if (missed.length) {
        setToggleError(`${check.check_key}: switched off, but its open findings were not dismissed -- ${missed[0].error}`)
      }
      await loadChecks()
    } catch (e) {
      setToggleError(`${check.check_key}: could not switch ${check.enabled === false ? 'on' : 'off'} -- ${loadErrorMessage(e)}`)
    }
  }

  const regenerateObs = async () => {
    if (!scope.customer) return alert('Select a customer first')
    setLoading(true)
    try {
      await apiFetch(`/api/observations/${scope.customer}/generate`)
      await loadLiveObs()
    } catch (e) { alert(`Regen failed: ${e.message}`) }
    setLoading(false)
  }

  const categories = [...new Set(checks.map(c => c.category || ''))].filter(Boolean).sort()

  const filtered = (activeView === 'catalog' ? checks : liveObs).filter(c => {
    const text = activeView === 'catalog'
      ? `${c.check_key} ${c.description} ${c.category}`
      : `${c.title} ${c.detail} ${c.category}`
    return (!search || text.toLowerCase().includes(search.toLowerCase()))
      && (!catFilter || (c.category || '') === catFilter)
  })

  const hasSQLTemplate = (c) => c.sql_template || c.drill_sql_template

  return (
    <div className="ak-tab-body">
      <div className="ak-filter-bar">
        <div className="ak-view-toggle">
          <button className={activeView === 'catalog' ? 'active' : ''} onClick={() => setActiveView('catalog')}>
            Check Definitions <span className="ak-count">{checks.length}</span>
          </button>
          <button className={activeView === 'live' ? 'active' : ''} onClick={() => setActiveView('live')}>
            Live Observations {scope.customer && <span className="ak-count">{liveObs.length}</span>}
          </button>
        </div>
        <input className="ak-search" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="ak-select-sm" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {activeView === 'live' && (
          <VPCSelectors vendors={vendors} customers={customers}
            vendor={scope.vendor} product={scope.product} customer={scope.customer}
            onChange={(k, v) => setScope(p => ({ ...p, [k]: v }))}
          />
        )}
        {activeView === 'live' && scope.customer && (
          <button className="ak-btn-green" onClick={regenerateObs} disabled={loading}>
            {loading ? 'Running…' : '↺ Regenerate'}
          </button>
        )}
      </div>

      {toggleError && <div className="ak-test-err" role="alert">{toggleError}</div>}

      {loading ? <div className="ak-loading">Loading…</div>
        : activeView === 'catalog' && checksError ? <LoadError what="checks" error={checksError} onRetry={loadChecks} />
        : activeView === 'live' && liveError ? <LoadError what="live observations" error={liveError} onRetry={loadLiveObs} />
        : (
        <div className="ak-table-wrap">
          <table className="ak-table">
            <thead>
              <tr>
                {activeView === 'catalog' ? <>
                  <th>Check Key</th><th>Category</th><th>Severity</th><th>SQL Override</th><th>Description</th><th>On</th><th></th>
                </> : <>
                  <th>Title</th><th>Category</th><th>Severity</th><th>Affected</th><th>SQL</th><th>Status</th><th></th>
                </>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="ak-empty">
                  {activeView === 'live' && !scope.customer ? 'Select a customer to view live observations' : 'No results'}
                </td></tr>
              )}
              {activeView === 'catalog' && filtered.map((c, i) => (
                <tr key={i}>
                  <td><code className="ak-code">{c.check_key}</code></td>
                  <td><span className="ak-cat">{c.category}</span></td>
                  <td><SevBadge sev={c.severity_default || c.severity} /></td>
                  <td>
                    {hasSQLTemplate(c)
                      ? <span className="ak-badge sev-info">✎ custom</span>
                      : <span className="ak-badge" style={{background:'#f3f4f6',color:'#9ca3af'}}>auto</span>
                    }
                  </td>
                  <td className="ak-desc">{c.description}</td>
                  <td><div className={`ak-toggle ${c.enabled !== false ? 'on' : ''}`} onClick={() => toggleEnabled(c)} /></td>
                  <td><button className="ak-btn-sm" onClick={() => openEdit(c, false)}>Edit</button></td>
                </tr>
              ))}
              {activeView === 'live' && filtered.map((o, i) => (
                <tr key={i}>
                  <td className="ak-desc">{o.title}</td>
                  <td><span className="ak-cat">{o.category}</span></td>
                  <td><SevBadge sev={o.severity} /></td>
                  <td className="ak-num">{o.affected_count?.toLocaleString() || '—'}</td>
                  <td>
                    {o.drill_sql
                      ? <span className="ak-badge sev-info">✓ drill SQL</span>
                      : <span className="ak-badge" style={{background:'#f3f4f6',color:'#9ca3af'}}>no drill</span>
                    }
                  </td>
                  <td><span className={`ak-badge ${(o.status && o.status !== 'open') ? 'sev-info' : 'sev-warning'}`}>{o.status && o.status !== 'open' ? o.status : 'open'}</span></td>
                  <td><button className="ak-btn-sm" onClick={() => openEdit(o, true)}>Edit SQL</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="ak-modal-overlay" onClick={() => setEditing(null)}>
          <div className="ak-modal ak-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="ak-modal-head">
              <div>
                <div className="ak-modal-title">
                  {editing._isLive ? editing.title : editing.check_key}
                </div>
                <div className="ak-modal-sub">
                  {editing._isLive
                    ? <>Live observation · {editing.category} · <code>{editing.check_key}</code></>
                    : <>Check definition · {editing.category}</>
                  }
                </div>
              </div>
              <button className="ak-modal-close" onClick={() => setEditing(null)}>✕</button>
            </div>

            <div className="ak-modal-body">
              {editing._isLive ? (
                // ── LIVE OBS: edit sql + drill_sql ──
                <>
                  <div className="ak-notice">
                    Edits here update this specific observation row. Re-generating observations will overwrite these changes. For permanent SQL changes, edit the Check Definition instead.
                  </div>
                  <div className="ak-field-group">
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <label>Count SQL (used to detect the issue)</label>
                      <button className="ak-btn-sm" onClick={() => testSQL(editForm.sql)} disabled={testing||!editForm.sql||!scope.customer}>
                        {testing ? 'Testing…' : '▶ Test'}
                      </button>
                    </div>
                    <textarea className="ak-textarea ak-mono ak-sql-editor" value={editForm.sql} rows={6}
                      onChange={e => setEditForm(p => ({ ...p, sql: e.target.value }))} />
                  </div>
                  <div className="ak-field-group">
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <label>Drill SQL (used for employee-level detail)</label>
                      <button className="ak-btn-sm" onClick={() => testSQL(editForm.drill_sql)} disabled={testing||!editForm.drill_sql||!scope.customer}>
                        {testing ? 'Testing…' : '▶ Test'}
                      </button>
                    </div>
                    <textarea className="ak-textarea ak-mono ak-sql-editor" value={editForm.drill_sql} rows={8}
                      onChange={e => setEditForm(p => ({ ...p, drill_sql: e.target.value }))} />
                  </div>
                  {testResult && <TestResultBlock result={testResult} />}
                  {!scope.customer && <div className="ak-notice">Select a customer above to enable SQL testing.</div>}
                </>
              ) : (
                // ── CHECK DEFINITION: metadata + SQL templates ──
                <>
                  <div className="ak-field-group">
                    <label>Title Template</label>
                    <input className="ak-input ak-mono" value={editForm.title_template}
                      onChange={e => setEditForm(p => ({ ...p, title_template: e.target.value }))} />
                  </div>
                  <div className="ak-field-group">
                    <label>Description</label>
                    <textarea className="ak-textarea" value={editForm.description} rows={2}
                      onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} />
                  </div>
                  <div className="ak-field-row">
                    <div className="ak-field-group">
                      <label>Default Severity</label>
                      <select className="ak-select" value={editForm.severity_default}
                        onChange={e => setEditForm(p => ({ ...p, severity_default: e.target.value }))}>
                        <option value="critical">Critical</option>
                        <option value="warning">Warning</option>
                        <option value="info">Info</option>
                      </select>
                    </div>
                    <div className="ak-field-group">
                      <label>Enabled</label>
                      <div className={`ak-toggle ${editForm.enabled ? 'on' : ''}`}
                        onClick={() => setEditForm(p => ({ ...p, enabled: !p.enabled }))} />
                    </div>
                  </div>

                  <div className="ak-sql-section-head">SQL Overrides <span className="ak-muted">(stored in ukg_pro_checks · use {"{{TableName}}"} tokens)</span></div>

                  <div className="ak-field-group">
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <label>Count SQL Template <span className="ak-muted">— must return affected_count column. Blank = auto-generated.</span></label>
                      <button className="ak-btn-sm" onClick={() => testSQL(editForm.sql_template)} disabled={testing||!editForm.sql_template||!scope.customer}>
                        {testing ? 'Testing…' : '▶ Test'}
                      </button>
                    </div>
                    <textarea className="ak-textarea ak-mono ak-sql-editor" value={editForm.sql_template} rows={6}
                      placeholder={"SELECT COUNT(*) AS affected_count FROM {{EmpComp}} WHERE ..."}
                      onChange={e => setEditForm(p => ({ ...p, sql_template: e.target.value }))} />
                  </div>
                  <div className="ak-field-group">
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <label>Drill SQL Template <span className="ak-muted">— employee-level rows for drill-down. Blank = auto-generated.</span></label>
                      <button className="ak-btn-sm" onClick={() => testSQL(editForm.drill_sql_template)} disabled={testing||!editForm.drill_sql_template||!scope.customer}>
                        {testing ? 'Testing…' : '▶ Test'}
                      </button>
                    </div>
                    <textarea className="ak-textarea ak-mono ak-sql-editor" value={editForm.drill_sql_template} rows={8}
                      placeholder={"SELECT emp.*, det.* FROM {{EmpComp}} emp JOIN {{EmpPers}} det ON emp.eeEmpNo = det.eepEmpNo WHERE ..."}
                      onChange={e => setEditForm(p => ({ ...p, drill_sql_template: e.target.value }))} />
                  </div>
                  {testResult && <TestResultBlock result={testResult} />}
                  {!scope.customer && <div className="ak-notice" style={{marginTop:8}}>Select a customer in the Live Observations view to enable SQL testing.</div>}
                </>
              )}
            </div>

            {saveError && <div className="ak-test-err" role="alert">{saveError}</div>}
            <div className="ak-modal-footer">
              <button className="ak-btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button className="ak-btn-green" onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2 — METRIC SQL
// ═══════════════════════════════════════════════════════════════════════════════
function MetricSQLTab({ customers, vendors }) {
  const [scope, setScope] = useState({ vendor: '', product: '', customer: '' })
  const [catalog, setCatalog] = useState(null)
  const [cachedSQL, setCachedSQL] = useState([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(null)
  const [editSQL, setEditSQL] = useState('')
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [modFilter, setModFilter] = useState('')

  // A failed load is an error with a Retry, never an empty metric list (item 201).
  const [catalogError, setCatalogError] = useState(null)
  const [cacheError, setCacheError] = useState(null)
  const loadCatalog = useCallback(() => {
    setCatalogError(null)
    apiFetch('/api/metrics/catalog/admin').then(d => setCatalog(d)).catch(e => {
      console.error(e)
      setCatalogError(loadErrorMessage(e))
    })
  }, [])
  useEffect(() => { loadCatalog() }, [loadCatalog])

  const loadCachedSQL = useCallback(async () => {
    if (!scope.customer) return
    setLoading(true)
    setCacheError(null)
    try {
      // The engine's cached SQL for this customer (metric_sql_cache, Supabase), read
      // server-side with the customer bound as a filter -- no SQL from the browser.
      const data = await apiFetch(`/api/metrics/${encodeURIComponent(scope.customer)}/cache`)
      setCachedSQL(listFrom(data, 'entries'))
    } catch (e) {
      console.error(e)
      setCachedSQL([])
      setCacheError(loadErrorMessage(e))
    }
    setLoading(false)
  }, [scope.customer])

  useEffect(() => { loadCachedSQL() }, [loadCachedSQL])

  // Build flat metric list from catalog
  const allMetrics = catalog ? Object.entries(catalog).flatMap(([mod, modDef]) =>
    Object.entries(modDef.metrics || {}).map(([key, def]) => ({ key, module: mod, ...def }))
  ) : []

  const modules = catalog ? Object.keys(catalog) : []

  const filteredMetrics = allMetrics.filter(m => {
    const matchSearch = !search || m.key.includes(search) || m.label?.toLowerCase().includes(search.toLowerCase())
    const matchMod = !modFilter || m.module === modFilter
    return matchSearch && matchMod
  })

  const openEdit = (metric) => {
    const cached = cachedSQL.find(c => c.metric_key === metric.key)
    setEditing(metric)
    setEditSQL(cached?.sql || '')
    setTestResult(null)
  }

  const testSQL = async () => {
    if (!scope.customer) return alert('Select a customer to test against')
    setTesting(true)
    setTestResult(null)
    try {
      const data = await apiFetch('/api/admin/query/raw', {
        method: 'POST',
        body: JSON.stringify({ sql: editSQL, customer_id: scope.customer })
      })
      setTestResult({ success: true, rows: data?.data || [], count: data?.row_count || 0 })
    } catch (e) {
      setTestResult({ success: false, error: e.message })
    }
    setTesting(false)
  }

  const saveSQL = async () => {
    if (!scope.customer) return alert('Select a customer to save cached SQL for')
    setSaving(true)
    try {
      await apiFetch(`/api/metrics/${encodeURIComponent(scope.customer)}/cache/${encodeURIComponent(editing.key)}`, {
        method: 'PUT',
        body: JSON.stringify({ sql: editSQL }),
      })
      await loadCachedSQL()
      setEditing(null)
    } catch (e) { alert(`Save failed: ${e.message}`) }
    setSaving(false)
  }

  const bustCache = async (metricKey) => {
    if (!scope.customer) return
    try {
      await apiFetch(`/api/metrics/${scope.customer}/cache/${metricKey}`, { method: 'DELETE' })
      await loadCachedSQL()
    } catch (e) { alert(`Bust failed: ${e.message}`) }
  }

  const bustAll = async () => {
    if (!scope.customer || !confirm(`Clear ALL cached metric SQL for ${scope.customer}?`)) return
    try {
      await apiFetch(`/api/metrics/${scope.customer}/cache`, { method: 'DELETE' })
      await loadCachedSQL()
    } catch (e) { alert(`Failed: ${e.message}`) }
  }

  return (
    <div className="ak-tab-body">
      <div className="ak-filter-bar">
        <VPCSelectors vendors={vendors} customers={customers}
          vendor={scope.vendor} product={scope.product} customer={scope.customer}
          onChange={(k, v) => setScope(p => ({ ...p, [k]: v }))}
        />
        <input className="ak-search" placeholder="Search metrics…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="ak-select-sm" value={modFilter} onChange={e => setModFilter(e.target.value)}>
          <option value="">All Modules</option>
          {modules.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        {scope.customer && (
          <button className="ak-btn-outline-sm" onClick={bustAll}>⚠ Bust All Cache</button>
        )}
      </div>

      {catalogError && <LoadError what="the metric catalog" error={catalogError} onRetry={loadCatalog} />}
      {cacheError && <LoadError what="cached SQL" error={cacheError} onRetry={loadCachedSQL} />}
      {loading ? <div className="ak-loading">Loading cache…</div> : catalogError ? null : (
        <div className="ak-table-wrap">
          <table className="ak-table">
            <thead>
              <tr>
                <th>Metric Key</th>
                <th>Module</th>
                <th>Label</th>
                <th>Display Type</th>
                <th>SQL Cached</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredMetrics.map((m, i) => {
                const cached = cachedSQL.find(c => c.metric_key === m.key)
                return (
                  <tr key={i}>
                    <td><code className="ak-code">{m.key}</code></td>
                    <td><span className="ak-cat">{m.module}</span></td>
                    <td>{m.label}</td>
                    <td><span className="ak-scope">{m.display_type || 'number'}</span></td>
                    <td>
                      {cached
                        ? <span className="ak-badge sev-info">✓ cached</span>
                        : <span className="ak-badge" style={{ background: '#f3f4f6', color: '#9ca3af' }}>none</span>
                      }
                    </td>
                    <td className="ak-muted">{cached?.updated_at ? new Date(cached.updated_at).toLocaleDateString() : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="ak-btn-sm" onClick={() => openEdit(m)}>
                          {cached ? 'Edit SQL' : 'Add SQL'}
                        </button>
                        {cached && scope.customer && (
                          <button className="ak-btn-sm ak-btn-red" onClick={() => bustCache(m.key)}>✕</button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* SQL Edit modal */}
      {editing && (
        <div className="ak-modal-overlay" onClick={() => setEditing(null)}>
          <div className="ak-modal ak-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="ak-modal-head">
              <div>
                <div className="ak-modal-title">{editing.label}</div>
                <div className="ak-modal-sub"><code>{editing.key}</code> · {editing.module} · {editing.display_type}</div>
              </div>
              <button className="ak-modal-close" onClick={() => setEditing(null)}>✕</button>
            </div>
            <div className="ak-modal-body">
              {!scope.customer && (
                <div className="ak-notice">Select a customer above to test and save SQL.</div>
              )}
              <div className="ak-field-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <label>SQL</label>
                  <span className="ak-muted" style={{ fontSize: 11 }}>
                    {editing.sql_question ? `Prompt: "${editing.sql_question.slice(0, 80)}…"` : ''}
                  </span>
                </div>
                <textarea className="ak-textarea ak-mono ak-sql-editor" value={editSQL} rows={10}
                  onChange={e => setEditSQL(e.target.value)}
                  placeholder="Paste or write SQL here. Use the table names from the customer's DuckDB schema." />
              </div>

              {testResult && (
                <div className={`ak-test-result ${testResult.success ? 'success' : 'error'}`}>
                  {testResult.success ? (
                    <>
                      <div className="ak-test-ok">✓ {testResult.count} rows returned</div>
                      {testResult.rows.length > 0 && (
                        <div className="ak-test-preview">
                          <table className="ak-table ak-table-sm">
                            <thead><tr>{Object.keys(testResult.rows[0]).map(k => <th key={k}>{k}</th>)}</tr></thead>
                            <tbody>{testResult.rows.slice(0, 5).map((r, i) => (
                              <tr key={i}>{Object.values(r).map((v, j) => <td key={j}>{String(v ?? '—')}</td>)}</tr>
                            ))}</tbody>
                          </table>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="ak-test-err">⚠ {testResult.error}</div>
                  )}
                </div>
              )}
            </div>
            <div className="ak-modal-footer">
              <button className="ak-btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button className="ak-btn-outline" onClick={testSQL} disabled={!editSQL || testing || !scope.customer}>
                {testing ? 'Testing…' : '▶ Test SQL'}
              </button>
              <button className="ak-btn-green" onClick={saveSQL} disabled={!editSQL || saving || !scope.customer}>
                {saving ? 'Saving…' : 'Save Cached SQL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3 — VENDOR CONTEXT (SQL seed, training pairs, Q&A)
// ═══════════════════════════════════════════════════════════════════════════════
function VendorContextTab({ customers, vendors }) {
  const [scope, setScope] = useState({ vendor: '', product: '', customer: '' })
  const [subTab, setSubTab] = useState('training')  // training | seed | qa-editor
  const [pairs, setPairs] = useState([])
  const [loading, setLoading] = useState(false)
  // A failed load is an error with a Retry, never "No training pairs found" (item 201).
  const [pairsError, setPairsError] = useState(null)
  const [search, setSearch] = useState('')
  const [scopeFilter, setScopeFilter] = useState('')
  // QA Editor
  const [qaForm, setQaForm] = useState({ question: '', sql: '', functional_area: '' })
  const [qaSaving, setQaSaving] = useState(false)
  const [qaResult, setQaResult] = useState(null)
  const [qaTesting, setQaTesting] = useState(false)
  const [qaTestResult, setQaTestResult] = useState(null)
  // Seed
  const [seedLoading, setSeedLoading] = useState(false)
  const [seedResult, setSeedResult] = useState(null)

  const loadPairs = useCallback(async () => {
    setLoading(true)
    setPairsError(null)
    try {
      const params = new URLSearchParams({ limit: '200' })
      if (scope.customer) params.set('customer_id', scope.customer)
      if (scopeFilter) params.set('scope', scopeFilter)
      const data = await apiFetch(`/api/admin/training/list?${params}`)
      setPairs(data?.pairs || [])
    } catch (e) {
      console.error(e)
      setPairs([])
      setPairsError(loadErrorMessage(e))
    }
    setLoading(false)
  }, [scope.customer, scopeFilter])

  useEffect(() => { if (subTab === 'training') loadPairs() }, [subTab, loadPairs])

  const deletePair = async (id) => {
    if (!confirm('Deactivate this training pair?')) return
    try {
      await apiFetch(`/api/admin/training/${id}`, { method: 'DELETE' })
      loadPairs()
    } catch (e) { alert(e.message) }
  }

  const testQA = async () => {
    if (!scope.customer) return alert('Select a customer to test against')
    setQaTesting(true)
    setQaTestResult(null)
    try {
      const data = await apiFetch('/api/admin/query/raw', {
        method: 'POST',
        body: JSON.stringify({ sql: qaForm.sql, customer_id: scope.customer })
      })
      setQaTestResult({ success: true, rows: data?.data || [], count: data?.row_count || 0 })
    } catch (e) {
      setQaTestResult({ success: false, error: e.message })
    }
    setQaTesting(false)
  }

  const saveQA = async () => {
    if (!qaForm.question || !qaForm.sql) return alert('Question and SQL are required')
    setQaSaving(true)
    setQaResult(null)
    try {
      const body = {
        question: qaForm.question,
        sql: qaForm.sql,
        customer_id: scope.customer || undefined,
        vendor_id: scope.vendor || undefined,
        product_id: scope.product || undefined,
        functional_area: qaForm.functional_area || undefined,
        skip_validation: false,
      }
      const data = await apiFetch('/api/admin/train/qa', { method: 'POST', body: JSON.stringify(body) })
      setQaResult(data)
      if (data.success) setQaForm({ question: '', sql: '', functional_area: '' })
    } catch (e) { setQaResult({ success: false, error: e.message }) }
    setQaSaving(false)
  }

  const runSeed = async () => {
    if (!scope.customer) return alert('Select a customer')
    setSeedLoading(true)
    setSeedResult(null)
    try {
      const data = await apiFetch(`/api/admin/train/seed/${scope.customer}`, { method: 'POST' })
      setSeedResult(data)
    } catch (e) { setSeedResult({ success: false, error: e.message }) }
    setSeedLoading(false)
  }

  const runSchemaTraining = async () => {
    if (!scope.customer) return alert('Select a customer')
    setSeedLoading(true)
    setSeedResult(null)
    try {
      const data = await apiFetch('/api/admin/train/schema', {
        method: 'POST',
        body: JSON.stringify({ customer_id: scope.customer, vendor: scope.vendor || undefined })
      })
      setSeedResult(data)
    } catch (e) { setSeedResult({ success: false, error: e.message }) }
    setSeedLoading(false)
  }

  const runComprehensive = async (force) => {
    if (!scope.customer) return alert('Select a customer')
    setSeedLoading(true)
    setSeedResult(null)
    try {
      const data = await apiFetch(`/api/admin/train/comprehensive?customer_id=${scope.customer}&force_retrain=${force}`, { method: 'POST' })
      setSeedResult(data)
    } catch (e) { setSeedResult({ success: false, error: e.message }) }
    setSeedLoading(false)
  }

  const filteredPairs = pairs.filter(p => {
    const text = `${p.question} ${p.sql_text} ${p.vendor_id} ${p.product_id}`
    return !search || text.toLowerCase().includes(search.toLowerCase())
  })

  const scopeColors = { global: 'sev-info', vendor: 'sev-warning', product: 'ak-badge-blue', customer: 'sev-critical' }

  return (
    <div className="ak-tab-body">
      <div className="ak-filter-bar">
        <VPCSelectors vendors={vendors} customers={customers}
          vendor={scope.vendor} product={scope.product} customer={scope.customer}
          onChange={(k, v) => setScope(p => ({ ...p, [k]: v }))}
        />
      </div>

      {/* Sub-tabs */}
      <div className="ak-subtabs">
        <button className={subTab === 'training' ? 'active' : ''} onClick={() => setSubTab('training')}>
          Training Pairs {pairs.length > 0 && <span className="ak-count">{pairs.length}</span>}
        </button>
        <button className={subTab === 'seed' ? 'active' : ''} onClick={() => setSubTab('seed')}>Seed & Train</button>
        <button className={subTab === 'qa-editor' ? 'active' : ''} onClick={() => setSubTab('qa-editor')}>Add Q&A Pair</button>
      </div>

      {/* Training pairs list */}
      {subTab === 'training' && (
        <>
          <div className="ak-filter-bar ak-filter-bar-sm">
            <input className="ak-search" placeholder="Search questions, SQL…" value={search} onChange={e => setSearch(e.target.value)} />
            <select className="ak-select-sm" value={scopeFilter} onChange={e => setScopeFilter(e.target.value)}>
              <option value="">All Scopes</option>
              <option value="global">Global</option>
              <option value="vendor">Vendor</option>
              <option value="product">Product</option>
              <option value="customer">Customer</option>
            </select>
            <button className="ak-btn-outline-sm" onClick={loadPairs}>↺ Refresh</button>
          </div>
          {loading ? <div className="ak-loading">Loading…</div> : pairsError ? (
            <LoadError what="training pairs" error={pairsError} onRetry={loadPairs} />
          ) : (
            <div className="ak-table-wrap">
              <table className="ak-table">
                <thead>
                  <tr>
                    <th>Question</th>
                    <th>Scope</th>
                    <th>Vendor</th>
                    <th>Product</th>
                    <th>Customer</th>
                    <th>Source</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPairs.length === 0 && (
                    <tr><td colSpan={8} className="ak-empty">No training pairs found</td></tr>
                  )}
                  {filteredPairs.map((p, i) => (
                    <tr key={i}>
                      <td className="ak-desc ak-question" title={p.sql_text}>{p.question || '—'}</td>
                      <td><span className={`ak-badge ${scopeColors[p.scope] || 'sev-info'}`}>{p.scope}</span></td>
                      <td className="ak-muted">{p.vendor_id || '—'}</td>
                      <td className="ak-muted">{p.product_id || '—'}</td>
                      <td className="ak-muted">{p.customer_id || '—'}</td>
                      <td><span className="ak-scope">{p.source}</span></td>
                      <td className="ak-muted">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</td>
                      <td><button className="ak-btn-sm ak-btn-red" onClick={() => deletePair(p.id)}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Seed & Train */}
      {subTab === 'seed' && (
        <div className="ak-seed-panel">
          <div className="ak-seed-notice">
            Run these in order for a new customer, or individually to refresh specific layers.
            All operations scope to the selected customer.
          </div>
          <div className="ak-seed-grid">
            <div className="ak-seed-card">
              <div className="ak-seed-card-title">Schema Training</div>
              <div className="ak-seed-card-desc">Ingests this customer's actual DuckDB table/column names and generates DDL training. Run after any new data upload.</div>
              <button className="ak-btn-green" disabled={!scope.customer || seedLoading} onClick={runSchemaTraining}>
                {seedLoading ? 'Running…' : 'Train Schema'}
              </button>
            </div>
            <div className="ak-seed-card">
              <div className="ak-seed-card-title">SQL Seed Data</div>
              <div className="ak-seed-card-desc">Generates Q&A pairs from customer data values — categorical columns, headcount, filter examples. Feeds NL→SQL translation.</div>
              <button className="ak-btn-green" disabled={!scope.customer || seedLoading} onClick={runSeed}>
                {seedLoading ? 'Running…' : 'Run Seed'}
              </button>
            </div>
            <div className="ak-seed-card">
              <div className="ak-seed-card-title">Comprehensive Training</div>
              <div className="ak-seed-card-desc">Runs schema + seed + auto-generated Q&A pairs in one pass. Skips pairs that already exist unless force is checked.</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="ak-btn-green" disabled={!scope.customer || seedLoading} onClick={() => runComprehensive(false)}>
                  {seedLoading ? 'Running…' : 'Run (skip existing)'}
                </button>
                <button className="ak-btn-outline" disabled={!scope.customer || seedLoading} onClick={() => runComprehensive(true)}>
                  Force Rebuild
                </button>
              </div>
            </div>
          </div>
          {seedResult && (
            <div className={`ak-seed-result ${seedResult.success ? 'success' : 'error'}`}>
              {seedResult.success
                ? `✓ ${seedResult.pairs_trained ?? seedResult.tables_trained ?? 'Done'} — ${seedResult.message || 'Complete'}`
                : `⚠ ${seedResult.error}`
              }
              {seedResult.skipped && <div style={{ marginTop: 4, fontSize: 11 }}>Already trained ({seedResult.existing_pairs} pairs). Use Force Rebuild to redo.</div>}
            </div>
          )}
        </div>
      )}

      {/* Q&A Editor */}
      {subTab === 'qa-editor' && (
        <div className="ak-qa-panel">
          <div className="ak-seed-notice">
            Add a validated Q&A pair to the training set. SQL is tested before saving unless skipped.
            Scope is determined by which of vendor/product/customer are selected above.
          </div>
          <div className="ak-field-group">
            <label>Question (natural language)</label>
            <input className="ak-input" value={qaForm.question}
              onChange={e => setQaForm(p => ({ ...p, question: e.target.value }))}
              placeholder="e.g. How many active employees are in California?" />
          </div>
          <div className="ak-field-group">
            <label>Functional Area <span className="ak-muted">(optional)</span></label>
            <input className="ak-input" value={qaForm.functional_area}
              onChange={e => setQaForm(p => ({ ...p, functional_area: e.target.value }))}
              placeholder="e.g. payroll, benefits, headcount" />
          </div>
          <div className="ak-field-group">
            <label>SQL</label>
            <textarea className="ak-textarea ak-mono ak-sql-editor" value={qaForm.sql} rows={8}
              onChange={e => setQaForm(p => ({ ...p, sql: e.target.value }))}
              placeholder="SELECT ..." />
          </div>

          {qaTestResult && (
            <div className={`ak-test-result ${qaTestResult.success ? 'success' : 'error'}`}>
              {qaTestResult.success
                ? <><div className="ak-test-ok">✓ {qaTestResult.count} rows</div>
                    {qaTestResult.rows.length > 0 && (
                      <div className="ak-test-preview">
                        <table className="ak-table ak-table-sm">
                          <thead><tr>{Object.keys(qaTestResult.rows[0]).map(k => <th key={k}>{k}</th>)}</tr></thead>
                          <tbody>{qaTestResult.rows.slice(0, 3).map((r, i) => (
                            <tr key={i}>{Object.values(r).map((v, j) => <td key={j}>{String(v ?? '—')}</td>)}</tr>
                          ))}</tbody>
                        </table>
                      </div>
                    )}</>
                : <div className="ak-test-err">⚠ {qaTestResult.error}</div>
              }
            </div>
          )}

          {qaResult && (
            <div className={`ak-seed-result ${qaResult.success ? 'success' : 'error'}`}>
              {qaResult.success ? '✓ Training pair saved' : `⚠ ${qaResult.error}`}
              {qaResult.validated && <span style={{ marginLeft: 8, fontSize: 11 }}>({qaResult.row_count} rows validated)</span>}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="ak-btn-outline" onClick={testQA} disabled={!qaForm.sql || qaTesting || !scope.customer}>
              {qaTesting ? 'Testing…' : '▶ Test SQL'}
            </button>
            <button className="ak-btn-green" onClick={saveQA} disabled={!qaForm.question || !qaForm.sql || qaSaving}>
              {qaSaving ? 'Saving…' : 'Save Training Pair'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const VENDORS = [
  { id: 'ukg', label: 'UKG', products: [
    { id: 'ukg_pro', label: 'UKG Pro (UltiPro)' },
    { id: 'ukg_wfm', label: 'UKG Workforce Management' },
    { id: 'ukg_ready', label: 'UKG Ready' },
  ]},
  { id: 'adp', label: 'ADP', products: [
    { id: 'adp_wfn', label: 'ADP Workforce Now' },
    { id: 'adp_enterprise', label: 'ADP Enterprise HR' },
  ]},
  { id: 'workday', label: 'Workday', products: [
    { id: 'workday_hcm', label: 'Workday HCM' },
  ]},
  { id: 'sap', label: 'SAP', products: [
    { id: 'sap_sf', label: 'SAP SuccessFactors' },
  ]},
  { id: 'oracle', label: 'Oracle', products: [
    { id: 'oracle_hcm', label: 'Oracle HCM Cloud' },
  ]},
  { id: 'paychex', label: 'Paychex', products: [
    { id: 'paychex_flex', label: 'Paychex Flex' },
  ]},
]

export default function AdminKnowledge() {
  const [tab, setTab] = useState('observations')
  const { customers, error: customersError, reload: reloadCustomers } = useCustomerList()

  const breadcrumbs = [{ label: 'Admin', to: '/admin' }, { label: 'Knowledge & Rules' }]
  const navActions = <Link to="/admin" className="nav-btn">← Admin</Link>

  const TABS = [
    { key: 'observations', label: 'Observation Checks' },
    { key: 'metrics', label: 'Metric SQL' },
    { key: 'context', label: 'Vendor Context & Training' },
  ]

  return (
    <div className="ak-root">
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
      <div className="ak-page">
        <div className="ak-page-head">
          <h1 className="ak-page-title">Knowledge & Rules</h1>
          <p className="ak-page-sub">Manage observation check definitions, metric SQL cache, and vendor-specific training context that drives SQL generation.</p>
        </div>
        <div className="ak-tabs">
          {TABS.map(t => (
            <button key={t.key} className={`ak-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        {customersError && <LoadError what="customers" error={customersError} onRetry={reloadCustomers} />}
        {tab === 'observations' && <ObservationChecksTab customers={customers} vendors={VENDORS} />}
        {tab === 'metrics' && <MetricSQLTab customers={customers} vendors={VENDORS} />}
        {tab === 'context' && <VendorContextTab customers={customers} vendors={VENDORS} />}
      </div>
    </div>
  )
}
